import clsx from 'clsx';
import type { Identifier, XYCoord } from 'dnd-core';
import { MouseEvent, useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';

import { DragItem, DragType } from '~components/generic/drag-and-drop/DragType';
import EditableField from '~components/generic/editable-field/EditableField/EditableField';
import useMoveModule from '~hooks/useMoveModule';
import { IEntityComponentProps } from '~interfaces/props';
import { convertToRawValue } from '~services/data-conversion';

/**
 * Calculate if the mouse position is in the former or latter half of the domNode
 * @return {boolean} `true` = before | `false` = after
 */
function calculateBeforeOrAfter(domNode: HTMLElement, offset: XYCoord): boolean {
  // Determine rectangle on screen
  const hoverBoundingRect = domNode.getBoundingClientRect();
  // Get horizontal middle
  const hoverMiddleX = (hoverBoundingRect.right - hoverBoundingRect.left) / 2;
  // Get pixels to the left
  const hoverClientX = offset.x - hoverBoundingRect.left;
  // return comparison
  return hoverClientX < hoverMiddleX;
}

export interface ITabItemHeader extends IEntityComponentProps {
  isActive: boolean;
  tabsEditing: boolean;
  onClick: (e: MouseEvent) => void;
}

export const TabItemHeader = (props: ITabItemHeader) => {
  const { isActive, onClick, data, onChange, onDelete, tabsEditing, index, entityId, fieldName, parentContext } = props;
  const refLi = useRef<HTMLLIElement>(null);

  const moveTab = useMoveModule({ parentId: parentContext, id: entityId, fieldName });

  // Drop handler
  const [{ handlerId }, dropTab] = useDrop<DragItem, void, { handlerId: Identifier | null; isOver: boolean }>({
    accept: DragType.TAB,
    collect: monitor => {
      return {
        handlerId: monitor.getHandlerId(),
        isOver: monitor.isOver(),
      };
    },
    hover: (item, monitor) => {
      if (!refLi.current) {
        return;
      }
      if (!monitor.canDrop()) {
        return;
      }

      const dragIndex = item.index;
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      const isBefore = calculateBeforeOrAfter(refLi.current, monitor.getClientOffset());
      // Only perform the move when the mouse has crossed half of the items height
      // When dragging downwards, only move when the cursor is below 50%
      // When dragging upwards, only move when the cursor is above 50%

      // Dragging downwards
      if (dragIndex < hoverIndex && isBefore) {
        return;
      }

      // Dragging upwards
      if (dragIndex > hoverIndex && !isBefore) {
        return;
      }

      // Time to actually perform the action
      moveTab(dragIndex);

      // Note: we're mutating the monitor item here!
      // Generally it's better to avoid mutations,
      // but it's good here for the sake of performance
      // to avoid expensive index searches.
      item.index = hoverIndex;
    },
  });

  // Drag handler
  const refDrag = useRef<HTMLDivElement>(null);
  const [_, dragConnector, dragPreview] = useDrag<DragItem>({
    type: DragType.TAB,
    item: { id: entityId, index },
    canDrag: () => tabsEditing,
  });
  dragConnector(refDrag);

  if (!data) {
    return null;
  }

  // Wrap drag ref
  dragPreview(dropTab(refLi));

  const clsTab = clsx('c-tabs__heading u-margin-right-sm', isActive && 'c-tabs__heading--active');

  return (
    <li ref={refLi} className={clsTab} style={{ verticalAlign: 'top' }} data-handler-id={handlerId}>
      {!tabsEditing ? (
        <a
          onClick={onClick}
          dangerouslySetInnerHTML={{
            __html: convertToRawValue(data.attributes.field_title.value),
          }}
        />
      ) : (
        <EditableField
          className={{
            'c-tabs__heading-link': true,
            'u-inline-block': true,
            'u-vertical-middle': true,
          }}
          hide={data.attributes.field_hide_title}
          entityType={data.type}
          fieldName="attributes.field_title.value"
          data={data.attributes.field_title.value}
          editing={tabsEditing}
          onChange={onChange}
          disableDrop={true}
        />
      )}
      {tabsEditing && (
        <span className="u-inline-block u-vertical-middle">
          <span style={{ textDecoration: 'none', marginRight: '0.33em' }} onClick={onDelete}>
            <i className="c-icon-custom c-icon-custom--vertical-centered c-icon-custom--trash" />
            <span className="u-sr-only">Delete</span>
          </span>
          <span ref={refDrag} className="cursor-grab">
            <i className="c-icon-custom c-icon-custom--vertical-centered c-icon-custom--handle"></i>
            <span className="u-sr-only">Drag</span>
          </span>
        </span>
      )}
    </li>
  );
};

export default TabItemHeader;
